import React, { useMemo, useState } from 'react';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next/';
import { GetStaticPropsContext } from 'next';

import {
  ADVERTISEMENT_DEVICE,
  ADVERTISEMENT_TYPE,
  FB_APP_ID,
  PageProps,
  XL_REVALIDATION_TIME,
  __CLIENT_SIDE__,
} from '@constants/index';
import Content from '@utils/Content';
import {
  DestinationLinksProvider,
  FooterLinksProvider,
  SiteLinksProvider,
  useAdvertisement,
  useHeadings,
  useLocale,
} from '@hooks/index';

import TipCTAProps from '@components/containers/TipCTA/TipCTA.props';
import RelatedTipListProps from '@components/lists/RelatedTipList/RelatedTipList.props';

import styles from './Tip.module.scss';
import { TContentElement } from '@models/content_elements/ContentElementTypes';
import Sidebar from '@components/containers/Sidebar/Sidebar';
import TableOfContents from '@components/containers/TableOfContents/TableOfContents';
import TipCTA from '@components/containers/TipCTA/TipCTA';
import { Meta } from '@components/layout/Meta/Meta';
import RelatedTipList from '@components/lists/RelatedTipList/RelatedTipList';
import TipList from '@components/lists/TipList/TipList';
import LoginModal from '@components/modals/LoginModal/LoginModal';
import RegisterModal from '@components/modals/RegisterModal/RegisterModal';
import BucketListHeader from '@components/ui/BucketListHeader/BucketListHeader';
import { BucketListTipModule, ITipPublicationFields } from 'modules/BucketListTipModule';
import GenericService from '@services/GenericService';
import GraphqlService from '@services/GraphqlService';
import MailchimpService from '@services/MailchimpService';
import PersonalService from '@services/PersonalService';
import CookieDialog from '@components/forms/CookieDialog/CookieDialog';
import Layout from '@components/layout/Layout/Layout';
import { Advertisement } from '@components/layout/Advertisement/Advertisement';
import { useAddView } from '@hooks/useAddView';
import { LazyLoad } from '@components/containers';

const { FacebookProvider } = require('react-facebook');

interface Props extends PageProps<ITipPublicationFields> {}

const BucketListItemPage = ({ fields, footerLinks, destinationLinks, siteLinks }: Props) => {
  useAdvertisement(
    ADVERTISEMENT_TYPE.ARTICLE,
    ADVERTISEMENT_DEVICE.DESKTOP_AND_MOBILE,
    `.${styles.Content} h2`,
    3,
  );
  useAdvertisement(
    ADVERTISEMENT_TYPE.ARTICLE,
    ADVERTISEMENT_DEVICE.DESKTOP_AND_MOBILE,
    `.${styles.Content} h2`,
    6,
  );
  useAddView(fields._id);

  const { t } = useTranslation();
  const locale = useLocale();
  const [isLoginModalOpen, setLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setRegisterModalOpen] = useState(false);
  const [relatedTips, setRelatedTips] = useState(fields.relatedTips);

  const filteredRelatedTips = useMemo(() => {
    return relatedTips.filter((tip: any) => !tip.url.includes(fields._slug));
  }, [relatedTips]);

  const headings = __CLIENT_SIDE__ ? useHeadings(`.${styles.Content} h2`) : [];

  const isDP = locale === 'nl-NL';
  const canLoadMore = fields.totalRelatedTips > relatedTips.length;

  const handleLoadMore = async (pageNr: number) => {
    if (!canLoadMore) {
      return;
    }

    const result = await BucketListTipModule.getRelatedTips(fields, pageNr);

    setRelatedTips((tips: any) => {
      return [...tips, ...result.items];
    });
  };

  const renderContent = () => {
    return fields.content.map((item: TContentElement, i: number) => {
      return (
        <div key={i} className={styles[item.__typename]}>
          {Content.renderPreprElement(item)}
        </div>
      );
    });
  };

  const categoryBannerProps: TipCTAProps =
    fields.category_banner && fields.category_banner[0]
      ? {
          title: fields.category_banner[0].display_title,
          subTitle: fields.category_banner[0].subtitle,
          backgroundImage: fields.category_banner[0].background_image[0].cdn_files[0]?.full,
          link: {
            url: fields.category_banner[0].button_url,
            label: fields.category_banner[0].button_label,
          },
        }
      : {
          title: t('DOMAIN.BUCKETLIST.MY_TIPS'),
          subTitle: t('DOMAIN.BUCKETLIST.READ_ALL_TIPS', { name: fields.parent_page[0].title }),
          backgroundImage: fields.parent_page[0].thumbnail[0]?.cdn_files[0]?.medium, // require('@assets/images/category_banner_background.jpg'),
          link: {
            url: `/${fields.parent_page[0]._slug}`,
            label: t('DOMAIN.BUCKETLIST.READ_TIPS'),
          },
        };

  const relatedTipListProps: RelatedTipListProps =
    fields.related_tips_landing_page && fields.related_tips_landing_page[0]
      ? {
          category: fields.related_tips_landing_page[0].title,
          tips: fields.relatedTips,
          buttonLink: `/${fields.related_tips_landing_page[0]._slug}`,
          restCount: fields.totalRelatedTips,
        }
      : {
          category: fields.parent_page[0].title,
          tips: fields.relatedTips,
          buttonLink: `/${fields.parent_page[0]._slug}`,
          restCount: fields.totalRelatedTips,
        };

  const handleRegisterClick = () => {
    setLoginModalOpen(false);
    setRegisterModalOpen(true);
  };

  return (
    <FacebookProvider appId={FB_APP_ID} xfbml>
      <FooterLinksProvider value={footerLinks}>
        <DestinationLinksProvider value={destinationLinks}>
          <SiteLinksProvider value={siteLinks}>
            <Layout paddings={false}>
              <Meta
                title={fields.meta_title || fields.title}
                description={fields.meta_description || fields.header_subtitle}
                image={fields.thumbnail[0].cdn_files[0]?.full}
              />
              <LoginModal
                isOpen={isLoginModalOpen}
                onClose={() => setLoginModalOpen(false)}
                onRegisterClick={handleRegisterClick}
              />
              <RegisterModal
                isOpen={isRegisterModalOpen}
                onClose={() => setRegisterModalOpen(false)}
              />
              <div className={styles.AdvertisementWrapper}>
                <Advertisement
                  className={styles.Advertisement}
                  advertisementType={ADVERTISEMENT_TYPE.ARTICLE}
                  advertisementDevice={ADVERTISEMENT_DEVICE.MOBILE}
                />
              </div>
              <div className={styles.BucketListTipPage}>
                {fields.thumbnail[0].cdn_files && fields.thumbnail[0].cdn_files[0] && (
                  <BucketListHeader
                    tipId={fields._id}
                    isLoadingBucketListCount={false}
                    isLoadingVisitedCount={false}
                    title={fields.title}
                    description={fields.header_subtitle}
                    backgroundImage={fields.thumbnail[0].cdn_files[0]?.full}
                    className={styles.Header}
                    bucketListCount={isDP ? fields._bookmarks : fields._likes}
                    imageAuthor={fields.thumbnail[0].author}
                    visitedCount={isDP ? fields._subscribes : fields._votes}
                    onUnauthorizedAdd={() => setLoginModalOpen(true)}
                  />
                )}

                <div className="container">
                  <div className="row">
                    <div className="col-lg-9">
                      <div className="intro" dangerouslySetInnerHTML={{ __html: fields.excerpt }} />
                      {headings.length > 0 && (
                        <div className={styles.Content}>
                          <div className={styles.TableOfContents}>
                            <TableOfContents items={headings} />
                          </div>
                        </div>
                      )}
                      <Advertisement
                        className={styles.Advertisement}
                        advertisementType={ADVERTISEMENT_TYPE.ARTICLE}
                      />
                      {!!fields.content && <div className={styles.Content}>{renderContent()}</div>}
                    </div>

                    {!!fields.sidebar && (
                      <div className={cn(styles.SideBarDesktop, 'col-lg-3')}>
                        <Advertisement advertisementType={ADVERTISEMENT_TYPE.SIDEBAR} />
                        <Sidebar sidebar={fields.sidebar} />
                      </div>
                    )}
                  </div>

                  {fields.relatedTips && fields.relatedTips.length > 0 && (
                    <div className="row">
                      <div className="col-md-12">
                        {fields.show_category_banner && (
                          <div className={styles.CategoryBanner}>
                            <TipCTA {...categoryBannerProps} />
                          </div>
                        )}
                        <div className={styles.RelatedTips}>
                          {fields.show_all_related_tips ? (
                            <LazyLoad callback={handleLoadMore} canLoadMore={canLoadMore}>
                              <TipList largeTips tips={filteredRelatedTips} shouldSort />
                            </LazyLoad>
                          ) : (
                            <RelatedTipList {...relatedTipListProps} />
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {!!fields.sidebar && (
                    <div className={cn(styles.SideBarMobile, 'col-lg-3')}>
                      <Advertisement advertisementType={ADVERTISEMENT_TYPE.SIDEBAR} />
                      <Sidebar sidebar={fields.sidebar} />
                    </div>
                  )}
                </div>
              </div>
            </Layout>
            <CookieDialog />
          </SiteLinksProvider>
        </DestinationLinksProvider>
      </FooterLinksProvider>
    </FacebookProvider>
  );
};

export default observer(BucketListItemPage);

export const getStaticProps = async ({ locale, preview, params }: GetStaticPropsContext) => {
  if (!params) {
    return {
      notFound: true,
    };
  }

  GraphqlService.setLocale(locale);
  PersonalService.setLocale(locale);
  GenericService.setLocale(locale);
  MailchimpService.setLocale(locale);

  GraphqlService.setPreview(!!preview);

  const page = await BucketListTipModule.getSinglePage(`${params.category}/${params.tip}`);

  if (!page || !page.fields) {
    return {
      notFound: true,
      revalidate: XL_REVALIDATION_TIME,
    };
  }

  return {
    props: page,
    revalidate: XL_REVALIDATION_TIME,
  };
};

export const getStaticPaths = async () => {
  const paths = {
    paths: [],
    fallback: 'blocking',
  };

  return paths;
};
